class RdmIgniteMentalEdge extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-panel]"));
    this.slider = this.querySelector("[data-slider]");

    if (!this.slider || this.panels.length === 0) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.activeIndex = 0;
    this.connectVideos(signal);

    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.activateSlide(index), {
        signal,
      });
      tab.addEventListener(
        "keydown",
        (event) => this.handleTabKeydown(event, index),
        { signal }
      );
    });

    this.slider.addEventListener(
      "swiper:ready",
      (event) => this.connectSwiper(event.detail.swiper),
      {
        signal,
      }
    );
    this.slider.addEventListener(
      "swiper:slide-change",
      (event) => {
        const swiper = event.detail?.swiper;
        if (!swiper) return;

        if (this.swiper !== swiper) this.connectSwiper(swiper);
        else this.syncTabs(swiper);
      },
      {
        signal,
      }
    );
    this.slider.addEventListener("scroll", () => this.handleFallbackScroll(), {
      passive: true,
      signal,
    });
    document.addEventListener(
      "shopify:block:select",
      (event) => this.handleBlockSelect(event),
      {
        signal,
      }
    );

    this.activePanelObserver = new MutationObserver(() =>
      this.syncFromActivePanel()
    );
    this.activePanelObserver.observe(this.slider, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });
    this.syncFromActivePanel();

    if (this.slider.swiper) this.connectSwiper(this.slider.swiper);
  }

  disconnectedCallback() {
    if (this.swiper && this.handleSwiperSlideChange) {
      this.swiper.off("slideChange", this.handleSwiperSlideChange);
    }
    this.activePanelObserver?.disconnect();
    this.videoObserver?.disconnect();
    this.videoStates?.forEach((state) => {
      state.visible = false;
      state.video.pause();
    });
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.abortController?.abort();
  }

  connectSwiper(swiper) {
    if (this.swiper && this.handleSwiperSlideChange) {
      this.swiper.off("slideChange", this.handleSwiperSlideChange);
    }

    this.swiper = swiper;
    this.handleSwiperSlideChange = () => this.syncTabs(swiper);
    swiper.on("slideChange", this.handleSwiperSlideChange);
    const requestedIndex = this.requestedIndex;
    this.requestedIndex = null;

    if (Number.isInteger(requestedIndex)) swiper.slideTo(requestedIndex, 0);
    this.syncTabs(swiper);
  }

  activateSlide(index) {
    if (!this.swiper) {
      this.requestedIndex = index;
      this.syncActiveIndex(index);
      this.panels[index]?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "nearest",
        inline: "start",
      });
      return;
    }

    this.swiper.slideTo(index);
  }

  syncTabs(swiper) {
    this.syncActiveIndex(swiper.realIndex);
  }

  syncFromActivePanel() {
    const activeIndex = this.panels.findIndex((panel) =>
      panel.classList.contains("swiper-slide-active")
    );

    if (activeIndex >= 0) this.syncActiveIndex(activeIndex);
  }

  handleFallbackScroll() {
    if (this.slider.classList.contains("swiper-initialized")) return;
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const sliderBounds = this.slider.getBoundingClientRect();
      const sliderCenter = sliderBounds.left + sliderBounds.width / 2;
      let activeIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.panels.forEach((panel, panelIndex) => {
        const panelBounds = panel.getBoundingClientRect();
        const panelCenter = panelBounds.left + panelBounds.width / 2;
        const distance = Math.abs(panelCenter - sliderCenter);

        if (distance < closestDistance) {
          activeIndex = panelIndex;
          closestDistance = distance;
        }
      });

      this.syncActiveIndex(activeIndex);
    });
  }

  syncActiveIndex(activeIndex) {
    this.activeIndex = activeIndex;
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === activeIndex;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === activeIndex;
      panel.setAttribute("aria-hidden", String(!isActive));
      panel.inert = !isActive;
    });
    this.syncVideos();
  }

  connectVideos(signal) {
    this.videoStates = this.panels.flatMap((panel, index) => {
      const video = panel.querySelector("video");
      if (!video) return [];

      return [
        {
          video,
          index,
          visible: false,
          blocked: false,
          pending: false,
        },
      ];
    });
    if (this.videoStates.length === 0) return;

    this.motionPreference = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    this.motionPreference.addEventListener(
      "change",
      () => this.syncVideos(),
      { signal }
    );
    document.addEventListener("visibilitychange", () => this.syncVideos(), {
      signal,
    });

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const state = this.videoStates.find(
            (item) => item.video === entry.target
          );
          if (state)
            state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        });
        this.syncVideos();
      },
      { threshold: [0, 0.01] }
    );

    this.videoStates.forEach((state) => {
      const { video } = state;
      video.muted = true;
      video.addEventListener(
        "play",
        () => {
          if (!this.shouldPlayVideo(state)) video.pause();
        },
        { signal }
      );
      this.videoObserver.observe(video);
    });
  }

  shouldPlayVideo(state) {
    return (
      this.isConnected &&
      state.visible &&
      state.index === this.activeIndex &&
      !document.hidden &&
      !state.blocked &&
      !this.motionPreference.matches
    );
  }

  syncVideos() {
    this.videoStates?.forEach((state) => {
      if (!this.shouldPlayVideo(state)) {
        state.video.pause();
        return;
      }
      if (!state.video.paused || state.pending) return;

      state.video.muted = true;
      state.pending = true;
      let interrupted = false;
      state.video
        .play()
        .catch((error) => {
          // An interrupted play is expected when the visitor changes slides.
          interrupted = error.name === "AbortError";
          if (!interrupted) state.blocked = true;
        })
        .finally(() => {
          state.pending = false;
          if (interrupted && this.shouldPlayVideo(state)) this.syncVideos();
        });
    });
  }

  handleTabKeydown(event, index) {
    let nextIndex = index;

    if (event.key === "ArrowRight") nextIndex = (index + 1) % this.tabs.length;
    else if (event.key === "ArrowLeft")
      nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = this.tabs.length - 1;
    else return;

    event.preventDefault();
    event.stopPropagation();
    this.tabs[nextIndex]?.focus();
    this.activateSlide(nextIndex);
  }

  handleBlockSelect(event) {
    const panel = event.target.closest?.("[data-panel]");
    if (!panel || !this.contains(panel)) return;

    const index = this.panels.indexOf(panel);
    if (index >= 0) this.activateSlide(index);
  }
}

if (!customElements.get("rdm-ignite-mental-edge")) {
  customElements.define("rdm-ignite-mental-edge", RdmIgniteMentalEdge);
}
